/**
 * Where a task lives: client → application → project → feature.
 *
 * ## Why four selects and not one
 *
 * The task dialog used to offer a single `Feature` select over every feature
 * in the portal, labelled `Project · Feature`. With Bailey's real data that is
 * **91 entries spanning two clients**, with no mention of the client or the
 * application that owns them — so the one field an owner most needs to get
 * right was the one the interface helped with least, and picking the wrong
 * client's feature looked exactly like picking the right one.
 *
 * Meanwhile the board's own filter bar has always offered Client and
 * Application dropdowns. The two levels you could filter by were the two you
 * could not select by. `docs/FINDINGS.md` F7.
 *
 * ## Why it is the same control when editing
 *
 * A task's feature was fixed at creation, so the only way to move work was to
 * delete it and lose its hours (F18). The same cascade now edits an existing
 * task, which is what makes a task movable at all.
 *
 * ## What it does not decide
 *
 * Whether the caller *may* move a task is the API's business — only an owner
 * may, and a task with hours logged against it cannot cross a client boundary
 * at all. This component renders disabled controls when `canMove` is false so
 * a contractor can still see where the work sits, and lets the server refuse
 * anything it gets wrong.
 */
import { useEffect, useMemo, useState } from 'react';

import { Select } from '@/components';
import type { SelectOption } from '@/components';
import { useApplications, useClients, useFeatures, useProjects } from '@/lib/queries';

export interface PlacementSelectProps {
  /** The chosen feature id, or `''` for nothing chosen yet. */
  value: string;
  onChange: (featureId: string) => void;
  /** False renders every level disabled but still readable. */
  canMove?: boolean;
  error?: string | null;
  /** Shown under the feature select — e.g. why a task cannot be moved. */
  hint?: string;
}

/**
 * The three levels above the feature, which are navigation rather than data.
 *
 * Deliberately **not** lifted to the caller. An earlier version let the task
 * dialog own this so it could reset it, and the reset raced this component's
 * own derivation: both wrote in the same commit, the reset won, and because it
 * wrote a shared constant the resulting state was `Object.is`-equal to what was
 * already there - so React skipped the re-render, this effect never ran again,
 * and a task with a perfectly good client showed "Choose a client" forever.
 *
 * It only bit on a **reopen**. The first open is slower than the queries, so
 * the derivation bailed out early, the reset landed first, and the data arriving
 * afterwards drove a clean derive. `docs/FINDINGS.md` F22.
 *
 * There is nothing for the caller to reset: the levels are a function of
 * `value`, and the component remounts with each dialog.
 */
interface PlacementDraft {
  clientId: string;
  applicationId: string;
  projectId: string;
}

const EMPTY_PLACEMENT: PlacementDraft = { clientId: '', applicationId: '', projectId: '' };

export function PlacementSelect({
  value,
  onChange,
  canMove = true,
  error = null,
  hint,
}: PlacementSelectProps) {
  const [draft, setDraft] = useState<PlacementDraft>(EMPTY_PLACEMENT);
  const clients = useClients();
  const applications = useApplications();
  const projects = useProjects();
  const features = useFeatures();

  const featureById = useMemo(
    () => new Map((features.data ?? []).map((f) => [f.id, f])),
    [features.data],
  );
  const projectById = useMemo(
    () => new Map((projects.data ?? []).map((p) => [p.id, p])),
    [projects.data],
  );
  const applicationById = useMemo(
    () => new Map((applications.data ?? []).map((a) => [a.id, a])),
    [applications.data],
  );

  /*
   * Snap the three parent selects to whatever feature is currently chosen.
   *
   * An existing task arrives knowing only its `featureId`, so the client,
   * application and project have to be walked back up the tree - and that
   * cannot happen until the four lists have loaded, which is why this is an
   * effect keyed on the data rather than a one-time initial state.
   */
  useEffect(() => {
    if (!value) return;
    const feature = featureById.get(value);
    const project = feature ? projectById.get(feature.projectId) : undefined;
    const application = project ? applicationById.get(project.applicationId) : undefined;
    if (!feature || !project || !application) return;

    setDraft({
      clientId: application.clientId,
      applicationId: application.id,
      projectId: project.id,
    });
    // `draft` is deliberately absent from the dependencies. It is what this
    // effect writes, so including it would loop; excluding it means the effect
    // runs only when the chosen feature or the lists behind it change, which
    // is exactly when the parents can need re-deriving.
  }, [value, featureById, projectById, applicationById]);

  const clientOptions = useMemo<SelectOption[]>(
    () => (clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [clients.data],
  );

  const applicationOptions = useMemo<SelectOption[]>(
    () =>
      (applications.data ?? [])
        .filter((a) => a.clientId === draft.clientId)
        .map((a) => ({ value: a.id, label: a.name })),
    [applications.data, draft.clientId],
  );

  const projectOptions = useMemo<SelectOption[]>(
    () =>
      (projects.data ?? [])
        .filter((p) => p.applicationId === draft.applicationId)
        .map((p) => ({ value: p.id, label: p.name })),
    [projects.data, draft.applicationId],
  );

  const featureOptions = useMemo<SelectOption[]>(
    () =>
      (features.data ?? [])
        .filter((f) => f.projectId === draft.projectId)
        .map((f) => ({ value: f.id, label: f.name })),
    [features.data, draft.projectId],
  );

  const loading =
    clients.isPending || applications.isPending || projects.isPending || features.isPending;

  /* Choosing a level invalidates everything under it, including the feature. */
  const pickClient = (clientId: string) => {
    setDraft({ clientId, applicationId: '', projectId: '' });
    onChange('');
  };
  const pickApplication = (applicationId: string) => {
    setDraft((current) => ({ ...current, applicationId, projectId: '' }));
    onChange('');
  };
  const pickProject = (projectId: string) => {
    setDraft((current) => ({ ...current, projectId }));
    onChange('');
  };

  return (
    <div className="flex flex-col gap-4 rounded-md border border-line bg-sunken/60 p-3">
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Client"
          required
          placeholder={loading ? 'Loading…' : 'Choose a client'}
          options={clientOptions}
          value={draft.clientId}
          disabled={!canMove || loading}
          onChange={(event) => pickClient(event.target.value)}
        />
        <Select
          label="Application"
          required
          placeholder={draft.clientId ? 'Choose an application' : 'Pick a client first'}
          options={applicationOptions}
          value={draft.applicationId}
          disabled={!canMove || !draft.clientId}
          onChange={(event) => pickApplication(event.target.value)}
        />
        <Select
          label="Project"
          required
          placeholder={draft.applicationId ? 'Choose a project' : 'Pick an application first'}
          options={projectOptions}
          value={draft.projectId}
          disabled={!canMove || !draft.applicationId}
          onChange={(event) => pickProject(event.target.value)}
        />
        <Select
          label="Feature"
          required
          placeholder={draft.projectId ? 'Choose a feature' : 'Pick a project first'}
          options={featureOptions}
          value={value}
          error={error}
          disabled={!canMove || !draft.projectId}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>

      <p className="text-[0.8125rem] text-muted">
        {hint ??
          'A task hangs off a feature, and the feature decides which client is charged for the hours logged against it.'}
      </p>
    </div>
  );
}
